import { useParams } from 'react-router'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useRun } from '@/hooks/use-runs'
import { useSSE } from '@/hooks/use-sse'
import { RunDetailHeader } from '@/components/run-detail/RunDetailHeader'
import { OverviewTab } from '@/components/run-detail/OverviewTab'
import { LogsTab } from '@/components/run-detail/LogsTab'
import { ResultsTab } from '@/components/run-detail/ResultsTab'
import { ProvenanceTab } from '@/components/run-detail/ProvenanceTab'

export function RunDetail() {
  const { fileId } = useParams<{ fileId: string }>()
  const { data: run, isLoading, error } = useRun(fileId)

  // SSE for status updates on active runs
  const isActive = run?.status === 'processing' || run?.status === 'queued'
  const { connectionState } = useSSE(fileId, {
    enabled: isActive,
    autoCloseOnComplete: true,
  })

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="h-4 w-32 bg-muted animate-pulse rounded" />
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="h-4 w-full bg-muted animate-pulse rounded" />
              <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
              <div className="h-4 w-1/2 bg-muted animate-pulse rounded" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Error state
  if (error || !run) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Run Not Found</h1>
        <p className="text-muted-foreground">
          {error?.message || `Could not find run with ID: ${fileId}`}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with filename, status, actions */}
      <div className="flex items-center gap-2">
        <RunDetailHeader run={run} />
        {isActive && connectionState === 'connected' && (
          <Badge variant="success" className="animate-pulse">
            Live
          </Badge>
        )}
      </div>

      {/* Tabbed content */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
          <TabsTrigger value="results">Results</TabsTrigger>
          <TabsTrigger value="provenance">Provenance</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab run={run} />
        </TabsContent>

        <TabsContent value="logs" className="mt-6">
          <LogsTab run={run} />
        </TabsContent>

        <TabsContent value="results" className="mt-6">
          <ResultsTab run={run} />
        </TabsContent>

        <TabsContent value="provenance" className="mt-6">
          <ProvenanceTab run={run} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
